import { FC, Fragment } from 'react'
import { GRAY_1000, MOBILE_VIEW_QUERY, WHITE } from 'constants/styling/theme'
import { Slide, Stack } from '@mui/material'

import { MUIButton } from 'components/common/MUIButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'

interface Props {
  /** Whether the tool bar is open or not */
  isToolBarOpen: boolean
  /** Save callback */
  onSave: () => void
  /** Discard callback */
  onDiscard: () => void
  /** If buttons for Discard are disabled mode and loading icon rendered */
  isLoadingDiscard: boolean
  /** If buttons for Save  are disabled mode and loading icon rendered */
  isLoadingSave: boolean
}

/**
 * Save/discard floating tool bar component.
 * @example <SaveFloatingToolBar 
 *  isToolBarOpen={true} 
 *  onSave={() => console.log('Huraaaa')} 
 *  onDiscard={() => console.log('Noooooo')}
 * />
 */
export const SaveFloatingToolBar: FC<Props> = ({
  isToolBarOpen = false,
  onSave,
  onDiscard,
  isLoadingDiscard,
  isLoadingSave,
}) => {
  const isMobileView = useMediaQuery(MOBILE_VIEW_QUERY)
  const { t } = useTranslation(['common'])

  return (
    <Fragment>
      {isMobileView ?
        <Fragment>
          <Slide direction="up" in={isToolBarOpen} mountOnEnter unmountOnExit>
            <Stack
              width="60%"
              position="fixed"
              boxSizing="border-box"
              bottom="0"
              left="20%"
              bgcolor={GRAY_1000}
              borderRadius="2rem 2rem 0rem 0rem"
              padding="1.2rem 2rem"
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >

                <Stack
                  direction="row"
                  alignItems="center"
                  height="100%"
                  fontWeight={600}
                >
                  <MUIButton
                    type="darkSecondaryNoborder"
                    onClick={onDiscard}
                    disabled={isLoadingDiscard}
                  >
                    {t('common:discard_changes')}
                  </MUIButton>
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  height="100%"
                  fontWeight={600}
                >
                  <MUIButton
                    type="darkPrimary"
                    onClick={onSave}
                    disabled={isLoadingSave || isLoadingDiscard}
                    isLoading={isLoadingSave}
                  >
                    {t('common:save_changes')}
                  </MUIButton>
                </Stack>

              </Stack>
            </Stack>
          </Slide>
        </Fragment>
        :
        <Slide direction="up" in={isToolBarOpen} mountOnEnter unmountOnExit>
          <Stack
            width="100vw"
            position="fixed"
            boxSizing="border-box"
            bottom="1.6rem"
            left={0}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Stack
              width={'auto'}
              padding=".4rem .4rem"
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              borderRadius="4.8rem"
              bgcolor={GRAY_1000}
              fontSize="1.4rem"
              fontWeight={600}
              color={WHITE}
              boxSizing="border-box"
            >

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                height="100%"
                fontWeight={600}
              >
                <MUIButton
                  type="darkSecondaryNoborder"
                  onClick={onDiscard}
                  disabled={isLoadingDiscard}
                >
                  {t('common:discard_changes')}
                </MUIButton>
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                height="100%"
                fontWeight={600}
              >
                <MUIButton
                  type="darkPrimary"
                  onClick={onSave}
                  disabled={isLoadingSave || isLoadingDiscard}
                  isLoading={isLoadingSave}
                >
                  {t('common:save_changes')}
                </MUIButton>
              </Stack>

            </Stack>
          </Stack>
        </Slide>
      }
    </Fragment>
  )
}
